import { useEffect, useState } from "react";
import { useAsync } from "react-async";
import { useHistory } from "react-router-dom";
import { getUnreadMessages } from "../../../../api/services/messageService";
import { DashboardProps, DashboardPropsDefaults } from "../../types/dashboardProps.d";
import MessageDisplay from "../../../../api/models/messageDisplay";
import Caseload from "../../../../api/models/caseload";
import { getExpires504, getExpiresIep } from '../../../../api/services/studentService';
import Spinner from "../../../spinner";
import DataLink from "../../../DataLink";
import { useDispatch } from "react-redux";
import { updateUnreadMessagesCount } from "../../../../slices/userSlice";
import DOMPurify from 'dompurify';

const fetchExpiresIep30 = async (args: any) => await getExpiresIep(args[0], 0, 30, args[1]);
const fetchExpiresIep60 = async (args: any) => await getExpiresIep(args[0], 31, 60, args[1]);
const fetchExpiresIep90 = async (args: any) => await getExpiresIep(args[0], 61, 90, args[1]);
const fetchExpires50430 = async (args: any) => await getExpires504(args[0], 0, 30, args[1]);
const fetchExpires50460 = async (args: any) => await getExpires504(args[0], 31, 60, args[1]);
const fetchExpires50490 = async (args: any) => await getExpires504(args[0], 61, 90, args[1]);

const DashboardWidgetTherapistNotifications = ({ discipline, user }: DashboardProps): JSX.Element => {
    const [disciplineId, setDisciplineId] = useState<number>(discipline?.disciplineId ?? 1);
    const [messages, setMessages] = useState<MessageDisplay[]>([]);
    const [expiresIep30, setExpiresIep30] = useState<Caseload[]>([]);
    const [expiresIep60, setExpiresIep60] = useState<Caseload[]>([]);
    const [expiresIep90, setExpiresIep90] = useState<Caseload[]>([]);
    const [expires50430, setExpires50430] = useState<Caseload[]>([]);
    const [expires50460, setExpires50460] = useState<Caseload[]>([]);
    const [expires50490, setExpires50490] = useState<Caseload[]>([]);
    const history = useHistory();
    const id: number = user.userId!;
    const dispatch = useDispatch();

    const asyncMessages = useAsync({
        promiseFn: getUnreadMessages, id,
        onResolve: (r) => {
            const sanitizedMessages = r.data.messages.map(message => ({
                ...message,
                messageText: DOMPurify.sanitize(message.messageText)
            }));

            setMessages(sanitizedMessages)
            dispatch(updateUnreadMessagesCount(r.data?.messages?.length ?? 0))
        }
    });

    useEffect(() => {
        if (discipline && discipline?.disciplineId) {
            setDisciplineId(discipline?.disciplineId);
            asyncIep30.run(id, discipline?.disciplineId);
            asyncIep60.run(id, discipline?.disciplineId);
            asyncIep90.run(id, discipline?.disciplineId);
            async50430.run(id, discipline?.disciplineId);
            async50460.run(id, discipline?.disciplineId);
            async50490.run(id, discipline?.disciplineId);
        }
    }, [discipline]);

    const asyncIep30 = useAsync({ deferFn: fetchExpiresIep30, onResolve: (r) => setExpiresIep30(r.data) });
    const asyncIep60 = useAsync({ deferFn: fetchExpiresIep60, onResolve: (r) => setExpiresIep60(r.data) });
    const asyncIep90 = useAsync({ deferFn: fetchExpiresIep90, onResolve: (r) => setExpiresIep90(r.data) });
    const async50430 = useAsync({ deferFn: fetchExpires50430, onResolve: (r) => setExpires50430(r.data) });
    const async50460 = useAsync({ deferFn: fetchExpires50460, onResolve: (r) => setExpires50460(r.data) });
    const async50490 = useAsync({ deferFn: fetchExpires50490, onResolve: (r) => setExpires50490(r.data) });

    /**
     * Redirects to the caseloads page with the given caseloads, caseloadIds, schoolId, and districtId.
     *
     * @param {CaseloadDisplay[]} caseloads - the list of caseloads to display
     * @param {number[]} [caseloadIds] - the list of caseload ids to use for filtering
     * @param {number} [schoolId] - the id of the school to filter by
     * @param {number} [districtId] - the id of the district to filter by
     * @return {void} 
     */
    const redirectWithCaseloads = (caseloads: Caseload[], caseloadIds?: number[], schoolId?: number, districtId?: number, disciplineId?: number) => {
        return history.push(`/caseload`, { caseloads, caseloadIds, schoolId, districtId, disciplineId });
    }

    return (
        <>

            <div className={DashboardPropsDefaults.containerClassNames}>
                <div className="pb-1 dashboard-widget-title">
                    <h3>Notification</h3>
                </div>
                <div className={DashboardPropsDefaults.contentClassNames}>
                    <Spinner show={(asyncMessages.isPending ||
                        asyncIep30.isPending || asyncIep60.isPending || asyncIep90.isPending ||
                        async50430.isPending || async50460.isPending || async50490.isPending
                    )} />
                    {!(asyncMessages.isPending ||
                        asyncIep30.isPending || asyncIep60.isPending || asyncIep90.isPending ||
                        async50430.isPending || async50460.isPending || async50490.isPending
                    ) && (
                            <>
                                {/* Messages */}
                                <div>
                                    You have <DataLink href='/messages' options={{ messages }}>({messages.length})</DataLink> unread messages
                                </div>

                                {/* IEP */}
                                <h3 className="mt-4 text-sm">IEP Expires In:</h3>
                                <div>
                                    30 Days: <span className="mb-2 font-normal underline cursor-pointer hover:no-underline text-ptsblue-500"
                                        onClick={(e) => redirectWithCaseloads(expiresIep30, undefined, expiresIep30[0] !== undefined ? expiresIep30[0].schoolId : 0, expiresIep30[0] !== undefined ? expiresIep30[0].districtId : 0, disciplineId)}>{expiresIep30.length}
                                    </span>
                                </div>
                                <div>
                                    60 Days: <span className="mb-2 font-normal underline cursor-pointer hover:no-underline text-ptsblue-500"
                                        onClick={(e) => redirectWithCaseloads(expiresIep60, undefined, expiresIep60[0] !== undefined ? expiresIep60[0].schoolId : 0, expiresIep60[0] !== undefined ? expiresIep60[0].districtId : 0, disciplineId)}>{expiresIep60.length}
                                    </span>
                                </div>
                                <div>
                                    90 Days: <span className="mb-2 font-normal underline cursor-pointer hover:no-underline text-ptsblue-500"
                                        onClick={(e) => redirectWithCaseloads(expiresIep90, undefined, expiresIep90[0] !== undefined ? expiresIep90[0].schoolId : 0, expiresIep90[0] !== undefined ? expiresIep90[0].districtId : 0, disciplineId)}>{expiresIep90.length}
                                    </span>
                                </div>

                                {/* 504 */}
                                <h3 className="mt-4 text-sm">504 Expires In:</h3>
                                <div>
                                    30 Days: <span className="mb-2 font-normal underline cursor-pointer hover:no-underline text-ptsblue-500"
                                        onClick={(e) => redirectWithCaseloads(expires50430, undefined, expires50430[0] !== undefined ? expires50430[0].schoolId : 0, expires50430[0] !== undefined ? expires50430[0].districtId : 0, disciplineId)}>{expires50430.length}
                                    </span>
                                </div>
                                <div>
                                    60 Days: <span className="mb-2 font-normal underline cursor-pointer hover:no-underline text-ptsblue-500"
                                        onClick={(e) => redirectWithCaseloads(expires50460, undefined, expires50460[0] !== undefined ? expires50460[0].schoolId : 0, expires50460[0] !== undefined ? expires50460[0].districtId : 0, disciplineId)}>{expires50460.length}
                                    </span>
                                </div>
                                <div>
                                    90 Days: <span className="mb-2 font-normal underline cursor-pointer hover:no-underline text-ptsblue-500"
                                        onClick={(e) => redirectWithCaseloads(expires50490, undefined, expires50490[0] !== undefined ? expires50490[0].schoolId : 0, expires50490[0] !== undefined ? expires50490[0].districtId : 0, disciplineId)}>{expires50490.length}
                                    </span>
                                </div>
                            </>
                        )}
                </div>
            </div>
        </>
    );
};

export default DashboardWidgetTherapistNotifications;
